/* Footer kick-about: sports balls (plus a vinyl record and a film reel) that drop in when the
   footer comes into view and can be grabbed, thrown and tapped. Plain canvas, no libraries. */
(() => {
  const box = document.querySelector('[data-playground]');
  if (!box) return;
  const canvas = box.querySelector('canvas');
  const ctx = canvas.getContext('2d');
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const GRAVITY = 1500;
  let W = 0;
  let H = 0;
  let scale = 1;
  let balls = [];
  let started = false;
  let running = false;
  let grabbed = null;
  let history = [];
  let last = 0;

  /* kind, radius (px at desktop), bounciness, mass factor */
  const KINDS = [
    ['football', 30, 0.72, 1], ['basketball', 34, 0.8, 1.2], ['rugby', 30, 0.55, 1.1], ['tennis', 15, 0.82, 0.3],
    ['reel', 28, 0.45, 0.9], ['cricket', 17, 0.45, 0.6], ['golf', 12, 0.7, 0.25], ['vinyl', 30, 0.4, 0.8],
  ];

  function resize() {
    const r = box.getBoundingClientRect();
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    W = r.width;
    H = r.height;
    scale = W < 600 ? 0.78 : 1;
    canvas.width = Math.round(W * dpr);
    canvas.height = Math.round(H * dpr);
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    balls.forEach((b) => {
      b.r = b.base * scale;
      b.x = Math.min(Math.max(b.x, b.r), W - b.r);
      b.y = Math.min(b.y, H - b.r);
    });
    if (!running) draw();
  }

  function spawn() {
    balls = KINDS.map(([kind, base, bounce, mass], i) => {
      const r = base * scale;
      const lane = (W * 0.35) + ((i + 0.5) / KINDS.length) * (W * 0.6);
      return {
        kind, base, r, bounce, mass: mass * r * r,
        x: Math.min(W - r, Math.max(r, lane + (Math.random() - 0.5) * 30)),
        y: reduceMotion ? H - r : r + Math.random() * 40,
        vx: (Math.random() - 0.5) * 120, vy: 0, a: Math.random() * Math.PI * 2,
      };
    });
    balls.forEach((b) => {
      if (b.kind !== 'reel') return;
      b.tail = Array.from({ length: 11 }, (_, i) => ({ x: b.x - i * b.r * 0.42, y: b.y + b.r * 0.6 }));
      b.dir = -1;
    });
  }

  /* The film strip unrolling from the reel. Each frame works out where the strip should lie
     (flat along the floor behind a resting reel, streaming behind a flying one) and eases towards it. */
  function stepTail(b, dt) {
    const t = b.tail;
    const n = t.length;
    const seg = b.r * 0.42;
    const speed = Math.hypot(b.vx, b.vy);
    if (Math.abs(b.vx) > 25) b.dir = b.vx > 0 ? -1 : 1; /* the strip trails behind the way it is rolling */
    const dir = b.dir || -1;
    const target = [];
    const onFloor = b.y >= H - b.r - 3 && Math.abs(b.vy) < 60;
    if (onFloor || speed < 80) {
      target.push([b.x + dir * b.r * 0.62, b.y + b.r * 0.62]);
      const floorY = H - 3;
      let x = b.x + dir * b.r * 1.05;
      target.push([x, floorY]);
      for (let i = 2; i < n; i++) {
        x += dir * seg;
        const curl = i >= n - 2 ? (i - (n - 3)) * seg * 0.45 : 0; /* loose end curls up a little */
        target.push([x - dir * curl * 0.3, floorY - curl]);
      }
    } else {
      const ux = -b.vx / speed;
      const uy = -b.vy / speed;
      let x = b.x + ux * b.r * 0.9;
      let y = b.y + uy * b.r * 0.9;
      target.push([x, y]);
      for (let i = 1; i < n; i++) {
        x += ux * seg;
        y += uy * seg + i * seg * 0.12; /* droops under gravity */
        target.push([x, Math.min(y, H - 3)]);
      }
    }
    const k = Math.min(1, dt * 14);
    t.forEach((p, i) => {
      const [tx, ty] = target[i];
      if (i === 0) { p.x = tx; p.y = ty; return; }
      p.x += (tx - p.x) * k * (1 - i * 0.03);
      p.y += (ty - p.y) * k * (1 - i * 0.03);
      p.x = Math.min(Math.max(p.x, 2), W - 2);
    });
  }

  function drawTail(b) {
    const t = b.tail;
    const w = b.r * 0.62;
    const path = () => {
      ctx.beginPath();
      ctx.moveTo(t[0].x, t[0].y);
      for (let i = 1; i < t.length - 1; i++) {
        ctx.quadraticCurveTo(t[i].x, t[i].y, (t[i].x + t[i + 1].x) / 2, (t[i].y + t[i + 1].y) / 2);
      }
      ctx.lineTo(t[t.length - 1].x, t[t.length - 1].y);
    };
    ctx.save();
    ctx.lineJoin = 'round';
    ctx.lineCap = 'butt';
    ctx.strokeStyle = 'rgba(245,239,228,.45)'; ctx.lineWidth = w + 3; path(); ctx.stroke();
    ctx.strokeStyle = '#231B1E'; ctx.lineWidth = w; path(); ctx.stroke();
    ctx.strokeStyle = 'rgba(184,145,62,.32)'; ctx.lineWidth = w * 0.48; ctx.setLineDash([w * 0.62, w * 0.14]); path(); ctx.stroke();
    /* sprocket holes along both edges */
    ctx.setLineDash([w * 0.12, w * 0.16]);
    ctx.strokeStyle = 'rgba(245,239,228,.8)'; ctx.lineWidth = w * 0.12;
    [-1, 1].forEach((side) => {
      ctx.beginPath();
      for (let i = 0; i < t.length; i++) {
        const a = t[Math.max(0, i - 1)];
        const c = t[Math.min(t.length - 1, i + 1)];
        const dx = c.x - a.x;
        const dy = c.y - a.y;
        const d = Math.hypot(dx, dy) || 1;
        const ox = (-dy / d) * w * 0.36 * side;
        const oy = (dx / d) * w * 0.36 * side;
        ctx[i ? 'lineTo' : 'moveTo'](t[i].x + ox, t[i].y + oy);
      }
      ctx.stroke();
    });
    ctx.restore();
  }

  /* ---------- Drawing ---------- */
  function poly(cx, cy, r, n, rot) {
    ctx.beginPath();
    for (let i = 0; i < n; i++) {
      const t = rot + (i / n) * Math.PI * 2;
      ctx[i ? 'lineTo' : 'moveTo'](cx + Math.cos(t) * r, cy + Math.sin(t) * r);
    }
    ctx.closePath();
  }

  function drawBall(b) {
    const { r } = b;
    ctx.save();
    ctx.translate(b.x, b.y);
    /* contact shadow on the floor */
    const lift = Math.max(0, H - b.y - r);
    ctx.save();
    ctx.translate(0, H - b.y - 2);
    ctx.scale(1, 0.25);
    ctx.fillStyle = `rgba(0,0,0,${Math.max(0.05, 0.28 - lift / 900)})`;
    ctx.beginPath();
    ctx.arc(0, 0, r * (1 + lift / 600), 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();

    ctx.rotate(b.a);
    ctx.lineWidth = Math.max(1, r * 0.06);
    if (b.kind === 'rugby') {
      ctx.scale(1.3, 0.82);
      ctx.fillStyle = '#8A4B2A';
      ctx.beginPath(); ctx.arc(0, 0, r, 0, Math.PI * 2); ctx.fill();
      ctx.strokeStyle = '#F5EFE4';
      ctx.beginPath(); ctx.moveTo(-r * 0.45, 0); ctx.lineTo(r * 0.45, 0); ctx.stroke();
      for (let i = -3; i <= 3; i++) { ctx.beginPath(); ctx.moveTo(i * r * 0.13, -r * 0.12); ctx.lineTo(i * r * 0.13, r * 0.12); ctx.stroke(); }
      ctx.strokeStyle = 'rgba(0,0,0,.25)';
      ctx.beginPath(); ctx.arc(0, 0, r, 0, Math.PI * 2); ctx.stroke();
      ctx.restore();
      return;
    }
    ctx.beginPath();
    ctx.arc(0, 0, r, 0, Math.PI * 2);
    ctx.save();
    ctx.clip();
    switch (b.kind) {
      case 'football': {
        /* Classic panelled ball: a centre pentagon, five foreshortened pentagons around it, seams between */
        ctx.fillStyle = '#F7F4EE'; ctx.fillRect(-r, -r, r * 2, r * 2);
        const pent = (cx, cy, rad, rot, squash, toward) => {
          const pts = [];
          for (let i = 0; i < 5; i++) {
            const t = rot + (i / 5) * Math.PI * 2;
            let lx = Math.cos(t) * rad;
            const ly = Math.sin(t) * rad;
            lx *= squash; /* squash along the radial direction, as if curving away */
            pts.push([cx + lx * Math.cos(toward) - ly * Math.sin(toward), cy + lx * Math.sin(toward) + ly * Math.cos(toward)]);
          }
          return pts;
        };
        const fillPts = (pts) => { ctx.beginPath(); pts.forEach(([px, py], i) => ctx[i ? 'lineTo' : 'moveTo'](px, py)); ctx.closePath(); ctx.fill(); };
        ctx.fillStyle = '#1A1416';
        const centre = pent(0, 0, r * 0.3, -Math.PI / 2, 1, 0);
        fillPts(centre);
        ctx.strokeStyle = 'rgba(26,20,22,.55)';
        ctx.lineWidth = Math.max(1, r * 0.045);
        const outers = [];
        for (let i = 0; i < 5; i++) {
          const ang = -Math.PI / 2 + (i / 5) * Math.PI * 2 + Math.PI / 5;
          const o = pent(Math.cos(ang) * r * 0.86, Math.sin(ang) * r * 0.86, r * 0.3, Math.PI, 0.62, ang);
          outers.push(o);
          fillPts(o);
        }
        /* seams: centre corners out, then on to the nearest corners of the outer panels */
        centre.forEach(([vx, vy], i) => {
          const ang = Math.atan2(vy, vx);
          const wx = Math.cos(ang) * r * 0.6;
          const wy = Math.sin(ang) * r * 0.6;
          ctx.beginPath(); ctx.moveTo(vx, vy); ctx.lineTo(wx, wy); ctx.stroke();
          [outers[(i + 4) % 5], outers[i]].forEach((o) => {
            const near = o.reduce((m, q) => (Math.hypot(q[0] - wx, q[1] - wy) < Math.hypot(m[0] - wx, m[1] - wy) ? q : m));
            ctx.beginPath(); ctx.moveTo(wx, wy); ctx.lineTo(near[0], near[1]); ctx.stroke();
          });
        });
        break;
      }
      case 'basketball':
        ctx.fillStyle = '#D8682A'; ctx.fillRect(-r, -r, r * 2, r * 2);
        ctx.strokeStyle = '#2A1A12';
        ctx.beginPath(); ctx.moveTo(-r, 0); ctx.lineTo(r, 0); ctx.moveTo(0, -r); ctx.lineTo(0, r); ctx.stroke();
        ctx.beginPath(); ctx.arc(-r * 1.25, 0, r * 0.95, -0.9, 0.9); ctx.stroke();
        ctx.beginPath(); ctx.arc(r * 1.25, 0, r * 0.95, Math.PI - 0.9, Math.PI + 0.9); ctx.stroke();
        break;
      case 'tennis':
        ctx.fillStyle = '#D4E04B'; ctx.fillRect(-r, -r, r * 2, r * 2);
        ctx.strokeStyle = '#F7F4EE'; ctx.lineWidth = Math.max(1.4, r * 0.12);
        ctx.beginPath(); ctx.arc(-r * 1.05, 0, r * 0.75, -1.1, 1.1); ctx.stroke();
        ctx.beginPath(); ctx.arc(r * 1.05, 0, r * 0.75, Math.PI - 1.1, Math.PI + 1.1); ctx.stroke();
        break;
      case 'cricket':
        ctx.fillStyle = '#9E1B22'; ctx.fillRect(-r, -r, r * 2, r * 2);
        ctx.strokeStyle = '#F2E6D8'; ctx.lineWidth = Math.max(1, r * 0.07);
        ctx.beginPath(); ctx.moveTo(-r, -r * 0.1); ctx.lineTo(r, -r * 0.1); ctx.moveTo(-r, r * 0.1); ctx.lineTo(r, r * 0.1); ctx.stroke();
        for (let i = -6; i <= 6; i++) { ctx.beginPath(); ctx.moveTo(i * r * 0.14, -r * 0.2); ctx.lineTo(i * r * 0.14 + r * 0.05, -r * 0.1); ctx.stroke(); }
        break;
      case 'golf':
        ctx.fillStyle = '#FBFAF6'; ctx.fillRect(-r, -r, r * 2, r * 2);
        ctx.fillStyle = 'rgba(26,20,22,.14)';
        for (let i = 0; i < 14; i++) {
          const t = i * 2.4; const d = r * (0.25 + (i % 3) * 0.25);
          ctx.beginPath(); ctx.arc(Math.cos(t) * d, Math.sin(t) * d, r * 0.1, 0, Math.PI * 2); ctx.fill();
        }
        break;
      case 'vinyl': {
        ctx.fillStyle = '#151214'; ctx.fillRect(-r, -r, r * 2, r * 2);
        /* light catching the grooves: stays put while the record turns */
        ctx.save();
        ctx.rotate(-b.a);
        if (ctx.createConicGradient) {
          const sheen = ctx.createConicGradient(-Math.PI / 4, 0, 0);
          sheen.addColorStop(0, 'rgba(255,255,255,0)');
          sheen.addColorStop(0.08, 'rgba(255,255,255,.16)');
          sheen.addColorStop(0.16, 'rgba(255,255,255,0)');
          sheen.addColorStop(0.5, 'rgba(255,255,255,0)');
          sheen.addColorStop(0.58, 'rgba(255,255,255,.12)');
          sheen.addColorStop(0.66, 'rgba(255,255,255,0)');
          sheen.addColorStop(1, 'rgba(255,255,255,0)');
          ctx.fillStyle = sheen;
          ctx.fillRect(-r, -r, r * 2, r * 2);
        }
        ctx.restore();
        ctx.lineWidth = 0.6;
        for (let k = 0.4; k < 0.95; k += 0.035) {
          ctx.strokeStyle = 'rgba(0,0,0,.45)';
          ctx.beginPath(); ctx.arc(0, 0, r * k, 0, Math.PI * 2); ctx.stroke();
        }
        ctx.strokeStyle = 'rgba(255,255,255,.18)'; ctx.lineWidth = 1;
        ctx.beginPath(); ctx.arc(0, 0, r * 0.97, 0, Math.PI * 2); ctx.stroke();
        /* plain label */
        ctx.fillStyle = '#7A1E33'; ctx.beginPath(); ctx.arc(0, 0, r * 0.34, 0, Math.PI * 2); ctx.fill();
        ctx.strokeStyle = 'rgba(214,180,110,.8)'; ctx.lineWidth = Math.max(0.8, r * 0.025);
        ctx.beginPath(); ctx.arc(0, 0, r * 0.27, 0, Math.PI * 2); ctx.stroke();
        ctx.fillStyle = '#E9E2D4'; ctx.beginPath(); ctx.arc(0, 0, r * 0.05, 0, Math.PI * 2); ctx.fill();
        break;
      }
      case 'reel':
        ctx.fillStyle = '#C3C6CC'; ctx.fillRect(-r, -r, r * 2, r * 2);
        ctx.fillStyle = '#2A2024'; ctx.beginPath(); ctx.arc(0, 0, r * 0.82, 0, Math.PI * 2); ctx.fill(); /* wound film */
        ctx.fillStyle = '#C3C6CC'; ctx.beginPath(); ctx.arc(0, 0, r * 0.3, 0, Math.PI * 2); ctx.fill();
        for (let i = 0; i < 5; i++) {
          const t = (i / 5) * Math.PI * 2;
          ctx.save(); ctx.rotate(t);
          ctx.fillStyle = '#C3C6CC'; ctx.fillRect(-r * 0.07, -r * 0.92, r * 0.14, r * 0.66); /* spokes */
          ctx.restore();
        }
        ctx.fillStyle = '#8E929A'; ctx.beginPath(); ctx.arc(0, 0, r * 0.12, 0, Math.PI * 2); ctx.fill();
        ctx.strokeStyle = '#A3A7AE'; ctx.lineWidth = Math.max(1, r * 0.08);
        ctx.beginPath(); ctx.arc(0, 0, r * 0.94, 0, Math.PI * 2); ctx.stroke();
        break;
      default:
        break;
    }
    /* soft shading so they read as round (not the record: it is flat) */
    if (b.kind === 'vinyl') { ctx.restore(); ctx.restore(); return; }
    const g = ctx.createRadialGradient(-r * 0.35, -r * 0.4, r * 0.1, 0, 0, r * 1.05);
    g.addColorStop(0, 'rgba(255,255,255,.28)');
    g.addColorStop(0.55, 'rgba(255,255,255,0)');
    g.addColorStop(1, 'rgba(0,0,0,.32)');
    ctx.rotate(-b.a);
    ctx.fillStyle = g;
    ctx.fillRect(-r, -r, r * 2, r * 2);
    ctx.restore();
    ctx.restore();
  }

  function draw() {
    ctx.clearRect(0, 0, W, H);
    balls.forEach((b) => { if (b.tail) drawTail(b); });
    balls.forEach(drawBall);
  }

  /* ---------- Physics ---------- */
  function step(dt) {
    balls.forEach((b) => {
      if (b === grabbed) return;
      b.vy += GRAVITY * dt;
      b.vx *= Math.pow(0.85, dt);
      b.x += b.vx * dt;
      b.y += b.vy * dt;
      if (b.y > H - b.r) {
        b.y = H - b.r;
        b.vy = -b.vy * b.bounce;
        if (Math.abs(b.vy) < 40) b.vy = 0;
        b.vx *= 0.9; /* floor friction */
      }
      if (b.x < b.r) { b.x = b.r; b.vx = Math.abs(b.vx) * b.bounce; }
      if (b.x > W - b.r) { b.x = W - b.r; b.vx = -Math.abs(b.vx) * b.bounce; }
      if (b.y < b.r) { b.y = b.r; b.vy = Math.abs(b.vy) * b.bounce; } /* ceiling: balls stay in view */
      b.a += (b.vx / b.r) * dt;
    });
    balls.forEach((b) => { if (b.tail) stepTail(b, dt); });
    for (let i = 0; i < balls.length; i++) {
      for (let j = i + 1; j < balls.length; j++) {
        const p = balls[i];
        const q = balls[j];
        const dx = q.x - p.x;
        const dy = q.y - p.y;
        const min = p.r + q.r;
        const d2 = dx * dx + dy * dy;
        if (d2 >= min * min || d2 === 0) continue;
        const d = Math.sqrt(d2);
        const nx = dx / d;
        const ny = dy / d;
        const overlap = min - d;
        const pm = p === grabbed ? Infinity : p.mass;
        const qm = q === grabbed ? Infinity : q.mass;
        const pShare = pm === Infinity ? 0 : qm === Infinity ? 1 : qm / (pm + qm);
        p.x -= nx * overlap * pShare;
        p.y -= ny * overlap * pShare;
        q.x += nx * overlap * (1 - pShare);
        q.y += ny * overlap * (1 - pShare);
        const rel = (q.vx - p.vx) * nx + (q.vy - p.vy) * ny;
        if (rel > 0) continue;
        const e = Math.min(p.bounce, q.bounce);
        const invP = pm === Infinity ? 0 : 1 / pm;
        const invQ = qm === Infinity ? 0 : 1 / qm;
        if (!invP && !invQ) continue;
        const jImp = (-(1 + e) * rel) / (invP + invQ);
        p.vx -= jImp * invP * nx; p.vy -= jImp * invP * ny;
        q.vx += jImp * invQ * nx; q.vy += jImp * invQ * ny;
      }
    }
  }

  function loop(now) {
    if (!running) return;
    const dt = Math.min(0.033, (now - last) / 1000 || 0.016);
    last = now;
    step(dt / 2);
    step(dt / 2);
    draw();
    requestAnimationFrame(loop);
  }

  function start() {
    if (running) return;
    running = true;
    last = performance.now();
    requestAnimationFrame(loop);
  }

  /* ---------- Grab, throw and tap ---------- */
  const point = (e) => {
    const r = canvas.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top, t: performance.now() };
  };
  const ballAt = (p) => {
    for (let i = balls.length - 1; i >= 0; i--) {
      const b = balls[i];
      const hit = b.r + 10;
      if ((b.x - p.x) ** 2 + (b.y - p.y) ** 2 < hit * hit) return b;
    }
    return null;
  };
  /* On touch screens only stop the page scrolling when the finger lands on a ball */
  canvas.addEventListener('touchstart', (e) => {
    const t = e.touches[0];
    if (t && ballAt(point(t))) e.preventDefault();
  }, { passive: false });
  canvas.addEventListener('pointerdown', (e) => {
    const p = point(e);
    const b = ballAt(p);
    if (!b) return;
    grabbed = b;
    grabbed.vx = 0;
    grabbed.vy = 0;
    history = [p];
    canvas.setPointerCapture(e.pointerId);
    canvas.classList.add('is-grabbing');
    start();
  });
  canvas.addEventListener('pointermove', (e) => {
    const p = point(e);
    if (!grabbed) {
      canvas.style.cursor = ballAt(p) ? 'grab' : 'default';
      return;
    }
    grabbed.x = Math.min(Math.max(p.x, grabbed.r), W - grabbed.r);
    grabbed.y = Math.min(p.y, H - grabbed.r);
    history.push(p);
    if (history.length > 6) history.shift();
  });
  const release = () => {
    if (!grabbed) return;
    const a = history[0];
    const z = history[history.length - 1];
    const dt = Math.max(0.016, (z.t - a.t) / 1000);
    let vx = (z.x - a.x) / dt;
    let vy = (z.y - a.y) / dt;
    if (history.length < 3 || Math.hypot(z.x - a.x, z.y - a.y) < 6) {
      vx = (Math.random() - 0.5) * 300; /* a tap is a kick upwards */
      vy = -900 - Math.random() * 300;
    }
    const max = 2600;
    const speed = Math.hypot(vx, vy);
    if (speed > max) { vx *= max / speed; vy *= max / speed; }
    grabbed.vx = vx;
    grabbed.vy = vy;
    grabbed = null;
    canvas.classList.remove('is-grabbing');
  };
  canvas.addEventListener('pointerup', release);
  canvas.addEventListener('pointercancel', release);

  /* Drop the balls in the first time the footer shows; pause when it scrolls away */
  resize();
  spawn();
  draw();
  window.addEventListener('resize', resize);
  if ('IntersectionObserver' in window) {
    new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        if (!started) { started = true; }
        start();
      } else {
        running = false;
      }
    }, { threshold: 0.2 }).observe(box);
  } else {
    start();
  }
})();
